import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DateSelect from '../components/DateSelect';
import './HomePage.scss';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [errors, setErrors] = useState<{ name?: string; birthDate?: string }>({});

  const validateForm = (): boolean => {
    const newErrors: { name?: string; birthDate?: string } = {};
    if (!name.trim()) newErrors.name = t('home.errors.nameRequired');
    if (!birthDate) {
      newErrors.birthDate = t('home.errors.birthDateRequired');
    } else if (new Date(birthDate) > new Date()) {
      newErrors.birthDate = t('home.errors.invalidDate');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      navigate('/matrix', { state: { name: name.trim(), birthDate } });
    }
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="home-page">
      <div className="home-page__container">
        <nav className="home-page__jump" aria-label={t('home.jump.aria')}>
          <button type="button" onClick={() => scrollTo('start')}>{t('home.jump.start')}</button>
          <button type="button" onClick={() => scrollTo('how')}>{t('home.jump.how')}</button>
          <button type="button" onClick={() => scrollTo('plans')}>{t('home.jump.plans')}</button>
        </nav>

        <header className="home-page__hero" id="start">
          <p className="home-page__brand">{t('home.hero.title')}</p>
          <h1 className="home-page__hook">{t('home.hero.hook')}</h1>
          <p className="home-page__lead">{t('home.hero.subSubtitle')}</p>

          <form className="home-page__form" onSubmit={handleSubmit} noValidate>
            <div className="home-page__form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  {t('home.form.name')}
                </label>
                <input
                  id="name"
                  type="text"
                  className={`form-input ${errors.name ? 'form-input--error' : ''}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('home.form.namePlaceholder')}
                  autoComplete="given-name"
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="birthDate" className="form-label">
                  {t('home.form.birthDate')}
                </label>
                <DateSelect
                  id="birthDate"
                  value={birthDate}
                  onChange={setBirthDate}
                  maxYear={new Date().getFullYear()}
                  error={!!errors.birthDate}
                />
                {errors.birthDate && <span className="form-error">{errors.birthDate}</span>}
              </div>
            </div>

            <button type="submit" className="btn-primary home-page__submit">
              {t('home.form.calculate')}
            </button>
            <p className="home-page__form-note">{t('home.hero.formNote')}</p>
          </form>
        </header>

        <section className="home-page__section" id="how" aria-labelledby="how-heading">
          <h2 id="how-heading" className="home-page__section-title">
            {t('home.howItWorks.title')}
          </h2>
          <ol className="home-page__steps">
            <li>
              <span className="home-page__step-num">1</span>
              <span>{t('home.howItWorks.step1')}</span>
            </li>
            <li>
              <span className="home-page__step-num">2</span>
              <span>{t('home.howItWorks.step2')}</span>
            </li>
            <li>
              <span className="home-page__step-num">3</span>
              <span>{t('home.howItWorks.step3')}</span>
            </li>
            <li>
              <span className="home-page__step-num">4</span>
              <span>{t('home.howItWorks.step4')}</span>
            </li>
          </ol>
        </section>

        <section className="home-page__section" aria-labelledby="benefits-heading">
          <h2 id="benefits-heading" className="home-page__section-title">
            {t('home.capabilities.title')}
          </h2>
          <ul className="home-page__benefits">
            <li>
              <strong>{t('home.capabilities.calendar')}</strong>
              <span>{t('home.capabilities.calendarDesc')}</span>
            </li>
            <li>
              <strong>{t('home.capabilities.aiChat')}</strong>
              <span>{t('home.capabilities.aiChatDesc')}</span>
            </li>
            <li>
              <strong>{t('home.capabilities.compatibility')}</strong>
              <span>{t('home.capabilities.compatibilityDesc')}</span>
            </li>
          </ul>
        </section>

        <section className="home-page__section" id="plans" aria-labelledby="plans-heading">
          <h2 id="plans-heading" className="home-page__section-title">
            {t('home.premium.title')}
          </h2>
          <p className="home-page__section-lead">{t('home.premium.desc')}</p>
          <div className="home-page__plans">
            <article className="home-page__plan">
              <h3>{t('home.premium.basic')}</h3>
              <p className="home-page__plan-price">
                {t('home.premium.basicPrice')}
                <span>{t('home.premium.basicPeriod')}</span>
              </p>
              <p className="home-page__plan-desc">{t('home.premium.basicDesc')}</p>
              <button type="button" className="home-page__plan-cta" onClick={() => navigate('/subscription?plan=basic')}>
                {t('home.premium.cta')}
              </button>
            </article>
            <article className="home-page__plan home-page__plan--featured">
              <p className="home-page__plan-badge">{t('home.premium.featured')}</p>
              <h3>{t('home.premium.pro')}</h3>
              <p className="home-page__plan-price">
                {t('home.premium.proPrice')}
                <span>{t('home.premium.proPeriod')}</span>
              </p>
              <p className="home-page__plan-desc">{t('home.premium.proDesc')}</p>
              <button type="button" className="home-page__plan-cta" onClick={() => navigate('/subscription?plan=pro')}>
                {t('home.premium.cta')}
              </button>
            </article>
            <article className="home-page__plan">
              <h3>{t('home.premium.premium')}</h3>
              <p className="home-page__plan-price">
                {t('home.premium.premiumPrice')}
                <span>{t('home.premium.premiumPeriod')}</span>
              </p>
              <p className="home-page__plan-desc">{t('home.premium.premiumDesc')}</p>
              <button type="button" className="home-page__plan-cta" onClick={() => navigate('/subscription?plan=premium')}>
                {t('home.premium.cta')}
              </button>
            </article>
            <article className="home-page__plan">
              <h3>{t('home.premium.full2026')}</h3>
              <p className="home-page__plan-price">{t('home.premium.full2026Price')}</p>
              <p className="home-page__plan-desc">{t('home.premium.full2026Desc')}</p>
              <button type="button" className="home-page__plan-cta" onClick={() => navigate('/subscription?plan=full2026')}>
                {t('home.premium.cta')}
              </button>
            </article>
          </div>
        </section>

        <footer className="home-page__footer">
          <a href="https://t.me/NumerolifeMatrix_bot" target="_blank" rel="noopener noreferrer">
            {t('home.footer.telegram')}
          </a>
          <a href="https://instagram.com/numerolife" target="_blank" rel="noopener noreferrer">
            {t('home.footer.instagram')}
          </a>
          <Link to="/terms">{t('home.footer.privacy')}</Link>
          <Link to="/terms-of-service">{t('home.footer.termsOfService')}</Link>
          <button
            type="button"
            className="home-page__footer-link"
            onClick={() => window.dispatchEvent(new CustomEvent('numerolife:openCookiePreferences'))}
          >
            {t('home.footer.cookies')}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
